import { VITE_FORGE_URL } from '@constants';
import axios, { AxiosInstance } from 'axios';
import * as api from 'dragonmouse-shared/api/forge';
import {
	CreateFileRequest,
	CreateFileResponse,
	CreateStyleRequest,
	CreateStyleResponse,
	GetFrameSignedUrlsRequest,
	GetFrameSignedUrlsResponse,
	GetSiteDesignSystemInfoRequest,
	GetSiteDesignSystemInfoResponse,
	ImageGenerateRequest,
	ImageGenerateResponse,
	ImportFigmaRequest,
	ImportFigmaResponse,
	ImportImageRequest,
	ImportImageResponse,
	InlineSvgRequest,
	InlineSvgResponse,
} from 'dragonmouse-shared/api/forge';
import { ErrorCode } from 'dragonmouse-shared/constants';
import { VARIANTS_HEADER } from 'dragonmouse-shared/variants';
import {
	ChatStatus,
	DesignSystem,
	DsChat,
	Frame,
	GoogleFont,
	PartialOrganization,
	PartialSceneComment,
	Scene,
	SceneChat,
	SceneComment,
	SystemChatEntry,
	User,
} from 'dragonmouse-shared/interfaces';
import debounce from 'lodash/debounce';
import defer from 'lodash/defer';
import { toast } from 'react-toastify';
import {
	optimisticallyUpdateDesignSystems,
	optimisticallyUpdateDsChats,
	optimisticallyUpdateFrames,
	optimisticallyUpdateSceneChats,
	optimisticallyUpdateSceneComments,
	optimisticallyUpdateScenes,
	optimisticallyUpdateUser,
	resolveOptimisticallyUpdatedDesignSystems,
	resolveOptimisticallyUpdatedDsChats,
	resolveOptimisticallyUpdatedFrames,
	resolveOptimisticallyUpdatedSceneChats,
	resolveOptimisticallyUpdatedSceneComments,
	resolveOptimisticallyUpdatedScenes,
	resolveOptimisticallyUpdatedUser,
	updateActiveSceneDsLink,
	updateSubscription,
} from 'redux/db/actions';
import { OptimisticWrite, selectFrame } from 'redux/db/reducers';
import { designSystemEditingBlocked } from 'redux/db/util';
import {
	optimisticallyUpdateDsFiles,
	resolveOptimisticDsFileWrites,
	showAlertModal,
} from 'redux/mode/actions';
import { createDsFileWrites } from 'redux/mode/util/dsFiles';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import store from 'redux/store';
import { v4 as uuid } from 'uuid';
import { getVariantHeader } from 'variants';
import {
	trackCreativeAction,
	trackDesignSystemAction,
	trackEndpoint,
	trackSceneAction,
} from '../analytics';

// Displays an error message to the user if it appears to be appropriate to do so.
export const alertErrorMessageFromServer = (err: any, showToast: boolean = false) => {
	console.error(err);
	if (err.silent || !showToast) return;
	if (axios.isAxiosError(err)) {
		let msg = `Request failed: ${err.config?.method?.toUpperCase()} ${err.config?.url}`;
		if (err.response?.data?.message) {
			msg = err.response.data.message;
		}
		if (err.response?.data?.error) {
			msg = 'Error: ' + err.response.data.error;
		}

		// Note on `defer()`: Calling `alert()` can block the browser from updating the UI, so there are
		// cases when things like loading indicators don't stop even though we want them too. By
		// deferring the alert, we allow the browser to update the UI before showing the alert, which
		// should make the UI feel more responsive.
		defer(() => toast.error(msg));
	}
};

// Check for structured error codes and handle them with appropriate UI
// Returns true if the error was handled (caller should not show additional UI)
export const handleErrorCode = (err: any): boolean => {
	if (!axios.isAxiosError(err)) return false;

	const errorCode = err.response?.data?.errorCode;
	if (!errorCode) return false;

	switch (errorCode) {
		case ErrorCode.INSUFFICIENT_BALANCE:
			store.dispatch(
				showAlertModal({
					title: 'Credits not found',
					description:
						'You appear to be out of credits. Please visit your profile page for options to purchase more.',
					onSubmit: () => {},
				})
			);
			return true;
		default:
			return false;
	}
};

const alertOnError = <T>(fn: (...args: any[]) => Promise<T>) => {
	const wrappedFn = async (...args: any[]): Promise<T> => {
		try {
			return await fn(...args);
		} catch (e) {
			if (!handleErrorCode(e)) {
				alertErrorMessageFromServer(e, false); // Default: no toast
			}
			throw e;
		}
	};

	// We do something tricky here -- we make it so the user can call forge.addFrame().withToast()
	// and when they do so, this function is called inseead of the regular path
	// This provides a clean notation to requests toasts when actions fail
	wrappedFn.withToast = () => {
		return async (...args: any[]): Promise<T> => {
			try {
				return await fn(...args);
			} catch (e) {
				if (!handleErrorCode(e)) {
					alertErrorMessageFromServer(e, true); // Show toast
				}
				throw e;
			}
		};
	};

	// i had problems creating the right typescript type for this function with a .withToast() method
	// so i'm just returning it as any
	return wrappedFn as any;
};

// This function is probably the most gnarly part of optimistic updates. The
// idea here is to have shared code across frames, chats, and scenes that
// handles the most difficult case for optimistic updates: when you are updating
// both multiple objects at once, and multiple times per second. For example,
// dragging multiple frames around the canvas.

// This function returns a function that does the fancy stuff so we have one
// implementation across frames, chats, and scenes.

// Note: I'm using named handlers below to make the usage of this function more
// readable.
const createDbrHandler = <T extends { showToast?: boolean }>(handlers: {
	// We need a generic way to get the object id from the request. This would be
	// the frame id, scene, id, etc.
	getObjectId: (req: T) => string;
	// Called each time the returned handler is called. Use this to set and
	// updates optimistic updates. The same writeId will be returned for each call
	// up and until onRequest is called with the same writeId.
	onUpdate: (writeId: string, req: T) => void;
	// Called after a debounce delay. Use this to send data to the server. After
	// the writeId is used once here a new writeId will be used in subsequent onUpdate
	// calls.
	onRequest: (writeId: string, req: T) => Promise<any>;
}) => {
	const writeIds: Map<string, string> = new Map();
	const debouncedHandlers: Map<string, ReturnType<typeof debounce>> = new Map();

	return (req: T) => {
		const objectId = handlers.getObjectId(req);

		// Get or create a writeId for this object
		let writeId = writeIds.get(objectId);
		if (!writeId) {
			writeId = uuid();
			writeIds.set(objectId, writeId);
		}
		// Get or create debounced handler for this object
		let debouncedHandler = debouncedHandlers.get(objectId);
		if (!debouncedHandler) {
			debouncedHandler = debounce((writeId: string, req: T) => {
				handlers.onRequest(writeId, req);

				// Now that the request is actually going out we need a new writeId for
				// the next write (if there is one)
				writeIds.delete(objectId);

				// NOTE: we are not clearing the debounced handler here because it will
				// be reused for future requests but this is basically a small memory
				// leak. The number of objects is small enough that this is likely not a
				// problem, but we could consider adding a timeout (say 30 seconds)
				// after which the handler is cleaned up.
			}, 500);
			debouncedHandlers.set(objectId, debouncedHandler);
		}

		// Queue the request with current writeId
		debouncedHandler(writeId, req);

		// Call onUpdate. This is not debounced; it's meant to happen on every call.
		handlers.onUpdate(writeId, req);
	};
};

// const alertUnauthorized = debounce(
// 	() => {
// 		toast.error(
// 			<>
// 				Unauthorized request. Please log in again or request access to this instance.
// 				<br />
// 				<br />
// 				Make sure the login used on <a href="https://mightymeld.app">mightymeld.app</a> is the same
// 				as the one used to generate the mightymeld.secrets file you're using, and reload MightyMeld
// 				Studio.
// 			</>,
// 			{ autoClose: false }
// 		);
// 	},
// 	5000,
// 	{ leading: true, trailing: false }
// );

// frames.x/y/width/height, chats.x/y, and scene_comments.x/y are INTEGER columns.
// Float values from canvas math (drag, resize, transforms) raise a 22P02 in postgres.
// Round defensively at the API boundary so callers can't violate the constraint.
const roundIntegerPositionFields = <
	T extends Partial<Record<'x' | 'y' | 'width' | 'height', number>>
>(
	obj: T
): T => {
	const next: any = { ...obj };
	if (typeof next.x === 'number') next.x = Math.round(next.x);
	if (typeof next.y === 'number') next.y = Math.round(next.y);
	if (typeof next.width === 'number') next.width = Math.round(next.width);
	if (typeof next.height === 'number') next.height = Math.round(next.height);
	return next;
};

export class Forge {
	client: AxiosInstance;
	private cachedFonts: GoogleFont[] | null = null;

	constructor() {
		this.client = axios.create({
			baseURL: VITE_FORGE_URL,
			withCredentials: true,
			headers: {
				'x-studio-hash': import.meta.env.VITE_GIT_SHA || 'development',
			},
		});

		// Send Mixpanel's anonymous distinct_id to the backend for guest analytics.
		// Skip if the user is identified (distinct_id looks like an Auth0 ID).
		this.client.interceptors.request.use((config) => {
			try {
				const distinctId = window.mixpanel?.get_distinct_id();
				// Auth0 IDs contain '|' (e.g. 'google-oauth2|xxx', 'auth0|xxx') — only send anonymous IDs
				if (distinctId && !distinctId.includes('|')) {
					config.headers['x-mixpanel-distinct-id'] = distinctId;
				}
			} catch (e) {}
			return config;
		});

		// Forward dev variant values (src/variants) so endpoints can vary modes
		// per-request; absent whenever every axis is at its default.
		this.client.interceptors.request.use((config) => {
			const variantHeader = getVariantHeader();
			if (variantHeader) {
				config.headers[VARIANTS_HEADER] = variantHeader;
			}
			return config;
		});
	}

	//-----------------------------------------------------------------------------
	// auth
	//-----------------------------------------------------------------------------
	getAuthUser = alertOnError(async (): Promise<api.GetAuthUserResponse> => {
		const currentUrl = new URL(window.location.href);
		let requestUrl = '/auth/user';
		if (currentUrl.searchParams.has('frameId')) {
			requestUrl = `${requestUrl}?frameId=${currentUrl.searchParams.get('frameId')}`;
		}
		const resp = await this.client.get(requestUrl);
		return resp.data as api.GetAuthUserResponse;
	});

	authFigmaStatus = alertOnError(async (): Promise<api.AuthFigmaStatusResponse> => {
		const resp = await this.client.get('/auth/figma/status');
		const data = resp.data as api.AuthFigmaStatusResponse;
		return data;
	});

	disconnectFigma = alertOnError(async (): Promise<api.AuthFigmaDisconnectResponse> => {
		const resp = await this.client.post('/auth/figma/disconnect');
		return resp.data as api.AuthFigmaDisconnectResponse;
	});

	disconnectGithub = alertOnError(async (): Promise<api.AuthGithubDisconnectResponse> => {
		const resp = await this.client.post('/auth/github/disconnect');
		return resp.data as api.AuthGithubDisconnectResponse;
	});

	//-----------------------------------------------------------------------------
	// user
	//-----------------------------------------------------------------------------
	initializeUser = alertOnError(
		async (referralCode?: string, promoCode?: string): Promise<api.InitializeUserResponse> => {
			const resp = await this.client.post('/api/user/initialize', { referralCode, promoCode });
			const data = resp.data as api.InitializeUserResponse;
			return data;
		}
	);

	updateUser = async (req: api.UpdateUserRequest, showToast: boolean = false) => {
		// don't update the guest user
		if (req.update.id === 'guest') return;

		const write: OptimisticWrite<User> = {
			id: uuid(),
			operation: 'update',
			value: req.update as Partial<User> & { id: string },
		};
		store.dispatch(optimisticallyUpdateUser([write]));
		const requestWithWriteId: api.UpdateUserRequest = {
			update: { ...req.update, writeId: write.id } as any,
		};
		try {
			await this.client.patch('/api/user', requestWithWriteId);
		} catch (e) {
			// Add Sentry context if available
			if (typeof window !== 'undefined' && (window as any).Sentry) {
				(window as any).Sentry.setContext('updateUser_request', {
					userId: req.update.id,
					updateData: req.update,
					writeId: write.id,
				});
				(window as any).Sentry.setContext('updateUser_caller', {
					stack: new Error().stack, // Capture current call stack
				});
			}

			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedUser([write.id]));
			throw e;
		}
	};

	//-----------------------------------------------------------------------------
	// subscription
	//-----------------------------------------------------------------------------
	getSubscription = alertOnError(async (): Promise<api.GetUserSubscriptionResponse> => {
		const resp = await this.client.get('/api/user/subscription');
		const data = resp.data as api.GetUserSubscriptionResponse;
		store.dispatch(updateSubscription(data.subscription));
		return data;
	});

	//-----------------------------------------------------------------------------
	// scene
	//-----------------------------------------------------------------------------
	createScene = alertOnError(
		async (req: api.CreateSceneRequest): Promise<api.CreateSceneResponse> => {
			trackCreativeAction('new_scene');
			trackEndpoint('POST', '/api/scene');
			const resp = await this.client.post('/api/scene', req);
			const data = resp.data as api.CreateSceneResponse;
			return data;
		}
	);

	updateScene = async (req: api.UpdateSceneRequest, showToast: boolean = false) => {
		const write: OptimisticWrite<Scene> = {
			id: uuid(),
			operation: 'update',
			value: req.update,
		};
		store.dispatch(optimisticallyUpdateScenes([write]));
		const requestWithWriteId: api.UpdateSceneRequest = {
			update: { ...req.update, writeId: write.id },
		};
		try {
			trackEndpoint('PATCH', '/api/scene');
			await this.client.patch('/api/scene', requestWithWriteId);
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedScenes([write.id]));
			throw e;
		}
	};

	deleteScene = async (req: api.DeleteSceneRequest, showToast: boolean = false) => {
		trackSceneAction('delete');
		const write: OptimisticWrite<Scene> = {
			id: uuid(),
			operation: 'delete',
			value: { id: req.sceneId },
		};
		store.dispatch(optimisticallyUpdateScenes([write]));
		try {
			await this.client.delete('/api/scene', { data: req });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedScenes([write.id]));
			throw e;
		}
	};

	grantSceneAccess = alertOnError(
		async (req: api.GrantSceneAccessRequest): Promise<api.GrantSceneAccessResponse> => {
			trackSceneAction('grant_access');
			const resp = await this.client.post('/api/scene/grant-access', req);
			const data = resp.data as api.GrantSceneAccessResponse;
			return data;
		}
	);

	revokeSceneAccess = alertOnError(
		async (req: api.RevokeSceneAccessRequest): Promise<api.RevokeSceneAccessResponse> => {
			trackSceneAction('revoke_access');
			const resp = await this.client.post('/api/scene/revoke-access', req);
			const data = resp.data as api.RevokeSceneAccessResponse;
			return data;
		}
	);

	requestSceneAccess = alertOnError(
		async (sceneId: string): Promise<api.RequestSceneAccessResponse> => {
			const resp = await this.client.post('/api/scene/request-access', { sceneId });
			return resp.data as api.RequestSceneAccessResponse;
		}
	);

	getSceneUsers = alertOnError(
		async (req: api.GetSceneUsersRequest): Promise<api.GetSceneUsersResponse> => {
			const resp = await this.client.post('/api/scene/users', req);
			const data = resp.data as api.GetSceneUsersResponse;
			return data;
		}
	);

	getSceneOrganizations = alertOnError(
		async (sceneId: string): Promise<api.GetSceneOrganizationsResponse> => {
			const resp = await this.client.get(`/api/scene/${sceneId}/organizations`);
			const data = resp.data as api.GetSceneOrganizationsResponse;
			return data;
		}
	);

	addSceneToOrganization = alertOnError(
		async (
			sceneId: string,
			organizationId: string
		): Promise<api.AddSceneToOrganizationResponse> => {
			const resp = await this.client.post('/api/organization/scene', {
				organizationId,
				sceneId,
			});
			const data = resp.data as api.AddSceneToOrganizationResponse;
			return data;
		}
	);

	removeSceneFromOrganization = alertOnError(
		async (
			sceneId: string,
			organizationId: string
		): Promise<api.RemoveSceneFromOrganizationResponse> => {
			const resp = await this.client.delete('/api/organization/scene', {
				data: { organizationId, sceneId },
			});
			const data = resp.data as api.RemoveSceneFromOrganizationResponse;
			return data;
		}
	);

	getSceneDsLink = alertOnError(
		async (designSystemId: string): Promise<api.GetSceneDsLinkResponse> => {
			const resp = await this.client.get(`/api/design-system/${designSystemId}/scene-link`);
			return resp.data as api.GetSceneDsLinkResponse;
		}
	);

	// DesignSystem-Organization relationships
	getDesignSystemOrganizations = alertOnError(
		async (designSystemId: string): Promise<api.GetDesignSystemOrganizationsResponse> => {
			const resp = await this.client.get(`/api/design-system/${designSystemId}/organizations`);
			const data = resp.data as api.GetDesignSystemOrganizationsResponse;
			return data;
		}
	);

	addDesignSystemToOrganization = alertOnError(
		async (
			designSystemId: string,
			organizationId: string
		): Promise<api.AddDesignSystemToOrganizationResponse> => {
			const resp = await this.client.post('/api/organization/design-system', {
				organizationId,
				designSystemId,
			});
			const data = resp.data as api.AddDesignSystemToOrganizationResponse;
			return data;
		}
	);

	removeDesignSystemFromOrganization = alertOnError(
		async (
			designSystemId: string,
			organizationId: string
		): Promise<api.RemoveDesignSystemFromOrganizationResponse> => {
			const resp = await this.client.delete('/api/organization/design-system', {
				data: { organizationId, designSystemId },
			});
			const data = resp.data as api.RemoveDesignSystemFromOrganizationResponse;
			return data;
		}
	);

	linkSceneDesignSystem = alertOnError(
		async (req: api.LinkSceneDesignSystemRequest): Promise<api.LinkSceneDesignSystemResponse> => {
			const previousDsLink = store.getState().db.activeSceneDsLink;
			if (req.designSystemId === null) {
				// Unlinking: set to null
				store.dispatch(updateActiveSceneDsLink(null));
			}

			try {
				const resp = await this.client.post('/api/scene/link-design-system', req);
				const data = resp.data as api.LinkSceneDesignSystemResponse;
				return data;
			} catch (e) {
				// Rollback optimistic update on error
				store.dispatch(updateActiveSceneDsLink(previousDsLink));
				throw e;
			}
		}
	);

	// DBR = De-Bounce Remote
	// This function is meant to be called frequently, as when changing the
	// background color of a scene. The update will be applied optimistically
	// right away, over and over for each call. After a debounce, the last update
	// is sent to the server.
	dbrUpdateScene = createDbrHandler({
		getObjectId: (req: api.UpdateSceneRequest & { showToast?: boolean }) => req.update.id,
		onUpdate: (writeId, req: api.UpdateSceneRequest & { showToast?: boolean }) => {
			const write: OptimisticWrite<Scene> = {
				id: writeId,
				operation: 'update',
				value: req.update,
			};
			store.dispatch(optimisticallyUpdateScenes([write]));
		},
		onRequest: async (writeId, req: api.UpdateSceneRequest & { showToast?: boolean }) => {
			const requestWithWriteId: api.UpdateSceneRequest = {
				...req,
				update: { ...req.update, writeId },
			};
			try {
				trackEndpoint('PATCH', '/api/scene');
				await this.client.patch('/api/scene', requestWithWriteId);
			} catch (e) {
				alertErrorMessageFromServer(e, req.showToast || false);
				store.dispatch(resolveOptimisticallyUpdatedScenes([writeId]));
				throw e;
			}
		},
	});

	duplicateScene = alertOnError(
		async (req: api.DuplicateSceneRequest): Promise<api.DuplicateSceneResponse> => {
			trackCreativeAction('duplicate_scene');
			const resp = await this.client.post('/api/scene/duplicate', req);
			const data = resp.data as api.DuplicateSceneResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// frame
	//-----------------------------------------------------------------------------
	createFrame = async (
		req: Omit<api.CreateFrameRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		req = roundIntegerPositionFields(req);
		const write: OptimisticWrite<Frame> = {
			id: uuid(),
			operation: 'insert',
			value: {
				...req,
				// A real date will come from the database.
				// For the optimistic update this is good enough.
				createdAt: new Date(),
				// Image path is tricky. The path may exist in storage but forge will
				// try to validate that the frame actually has that image path saved in
				// the database before it will allow loading it. So if we try to display
				// it before the db has been updated, we'll be denied.
				imagePath: undefined,
			},
		};

		store.dispatch(optimisticallyUpdateFrames([write]));
		try {
			trackEndpoint('POST', '/api/frame');
			await this.client.post('/api/frame', { ...req, writeId: write.id });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
			throw e;
		}
	};

	updateFrame = async (req: api.UpdateFrameRequest, showToast: boolean = false) => {
		req = { ...req, update: roundIntegerPositionFields(req.update) };
		const write: OptimisticWrite<Frame> = {
			id: uuid(),
			operation: 'update',
			value: req.update,
		};
		store.dispatch(optimisticallyUpdateFrames([write]));
		const requestWithWriteId = { ...req, update: { ...req.update, writeId: write.id } };
		try {
			trackEndpoint('PATCH', '/api/frame');
			await this.client.patch('/api/frame', requestWithWriteId);
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
			throw e;
		}
	};

	deleteFrames = async (frameIds: string[], showToast: boolean = false) => {
		const deletedAt = new Date();
		const writes: OptimisticWrite<Frame>[] = frameIds.map((frameId) => ({
			id: uuid(),
			// Using 'update' instead of 'delete' because we expect the server to use
			// a soft delete which is actually an update.
			operation: 'update',
			value: { id: frameId, deletedAt },
		}));
		store.dispatch(optimisticallyUpdateFrames(writes));
		try {
			const data: api.DeleteFramesRequest = {
				ids: writes.map((w) => ({ frameId: w.value.id, writeId: w.id })),
			};
			await this.client.delete('/api/frame', { data });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedFrames(writes.map((w) => w.id)));
			throw e;
		}
	};

	retryFrame = alertOnError(async (req: api.RetryFrameRequest): Promise<api.RetryFrameResponse> => {
		const resp = await this.client.post('/api/frame/retry', req);
		const data = resp.data as api.RetryFrameResponse;
		return data;
	});

	addFrameEntry = async (
		req: Omit<api.AddFrameEntryRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		// visualEdit entries are routed to frame_chat on the server (not frame.entries),
		// and the writeId is dropped there. Doing an optimistic frame.entries write
		// here leaves an unresolved writeId that merges with concurrent metadata
		// writes (e.g. visualEditActive=true) and pins them in place forever — making
		// the version selection in the inspector show nothing after a later edit.
		if (req.entry.kind === 'visualEdit') {
			try {
				trackEndpoint('POST', '/api/frame/add-entry');
				await this.client.post('/api/frame/add-entry', req);
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				throw e;
			}
			return;
		}

		// Optimistic update: add the entry to what we currently see.
		// selectFrame applies prior in-flight optimistic writes — without it,
		// back-to-back add/update calls each compute newEntries from raw state
		// and clobber each other's optimistic entries.
		const frame = selectFrame(store.getState().db, req.frameId);
		if (frame) {
			// If an entry with this id already exists in optimistic state, replace
			// it (the saveEditToFrame caller may have decided to "create" but a
			// prior in-flight write already inserted it under the same id).
			const currentEntries = frame.entries || [];
			const existingIdx = currentEntries.findIndex((e) => e.id === req.entry.id);
			const newEntries =
				existingIdx >= 0
					? currentEntries.map((e) => (e.id === req.entry.id ? req.entry : e))
					: [...currentEntries, req.entry];

			const write: OptimisticWrite<Frame> = {
				id: uuid(),
				operation: 'update',
				value: { id: req.frameId, entries: newEntries },
			};
			store.dispatch(optimisticallyUpdateFrames([write]));

			try {
				trackEndpoint('POST', '/api/frame/add-entry');
				await this.client.post('/api/frame/add-entry', { ...req, writeId: write.id });
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
				throw e;
			}
		} else {
			// No frame in state, just make the request
			try {
				trackEndpoint('POST', '/api/frame/add-entry');
				await this.client.post('/api/frame/add-entry', req);
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				throw e;
			}
		}
	};

	updateFrameEntry = async (
		req: Omit<api.UpdateFrameEntryRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		// See addFrameEntry: visualEdit entries live in frame_chat and the forge
		// route drops the writeId, so an optimistic frame.entries write would
		// never resolve and would pin merged metadata writes in place.
		if (req.entry.kind === 'visualEdit') {
			try {
				trackEndpoint('POST', '/api/frame/update-entry');
				await this.client.post('/api/frame/update-entry', req);
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				throw e;
			}
			return;
		}

		// Optimistic update: replace the entry in what we currently see.
		// selectFrame respects prior in-flight optimistic writes so the entry
		// added by a recent addFrameEntry is visible to map() here.
		const frame = selectFrame(store.getState().db, req.frameId);
		if (frame) {
			const currentEntries = frame.entries || [];
			// Treat as upsert: if the entry isn't found (raw state hasn't seen the
			// add yet AND this code somehow ran without selectFrame seeing it),
			// append rather than silently dropping the update.
			const existingIdx = currentEntries.findIndex((e) => e.id === req.entry.id);
			const newEntries =
				existingIdx >= 0
					? currentEntries.map((e) => (e.id === req.entry.id ? req.entry : e))
					: [...currentEntries, req.entry];

			const write: OptimisticWrite<Frame> = {
				id: uuid(),
				operation: 'update',
				value: { id: req.frameId, entries: newEntries },
			};
			store.dispatch(optimisticallyUpdateFrames([write]));

			try {
				trackEndpoint('POST', '/api/frame/update-entry');
				await this.client.post('/api/frame/update-entry', { ...req, writeId: write.id });
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
				throw e;
			}
		} else {
			// No frame in state, just make the request
			try {
				trackEndpoint('POST', '/api/frame/update-entry');
				await this.client.post('/api/frame/update-entry', req);
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				throw e;
			}
		}
	};

	deleteFrameEntry = async (
		req: Omit<api.DeleteFrameEntryRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		// Optimistic update: remove the entry from what we currently see.
		// selectFrame so prior in-flight writes are respected.
		const frame = selectFrame(store.getState().db, req.frameId);
		// If the entry isn't in frame.entries, it lives in frame_chat (a VE) — the
		// forge route handles that without touching frames.write_id, so an
		// optimistic frame write here would never resolve. See addFrameEntry.
		const inFrameEntries = !!frame?.entries?.some((e) => e.id === req.entryId);
		if (frame && inFrameEntries) {
			const currentEntries = frame.entries || [];
			const newEntries = currentEntries.filter((e) => e.id !== req.entryId);

			const write: OptimisticWrite<Frame> = {
				id: uuid(),
				operation: 'update',
				value: { id: req.frameId, entries: newEntries },
			};
			store.dispatch(optimisticallyUpdateFrames([write]));

			try {
				trackEndpoint('POST', '/api/frame/delete-entry');
				await this.client.post('/api/frame/delete-entry', { ...req, writeId: write.id });
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
				throw e;
			}
		} else {
			// No frame in state, just make the request
			try {
				trackEndpoint('POST', '/api/frame/delete-entry');
				await this.client.post('/api/frame/delete-entry', req);
			} catch (e) {
				alertErrorMessageFromServer(e, showToast);
				throw e;
			}
		}
	};

	duplicateFrame = async (
		sourceFrame: Frame,
		newFrameId: string,
		sceneId: string,
		x: number,
		y: number,
		showToast: boolean = false
	) => {
		trackCreativeAction('duplicate_frame');

		// The optimistic frame starts out basically the same as the source frame.
		// But with a new id, sceneId, and position.
		const optimisticFrame: Omit<Frame, 'writeId'> = {
			...sourceFrame,
			id: newFrameId,
			sceneId,
			x,
			y,
			imagePath: undefined, // deprecated
			createdAt: new Date(),
		};
		const write: OptimisticWrite<Frame> = {
			id: uuid(),
			operation: 'insert',
			value: optimisticFrame,
		};
		store.dispatch(optimisticallyUpdateFrames([write]));
		const req: api.DuplicateFrameRequest = {
			sourceFrameId: sourceFrame.id,
			newFrameId,
			sceneId,
			x,
			y,
			writeId: write.id,
		};
		try {
			await this.client.post('/api/frame/duplicate', req);
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedFrames([write.id]));
			throw e;
		}
	};

	pasteFramesIntoScene = alertOnError(
		async (req: api.PasteFramesIntoSceneRequest): Promise<api.PasteFramesIntoSceneResponse> => {
			trackCreativeAction('paste_frames_into_scene');
			trackEndpoint('POST', '/api/scene/paste-frames');
			const resp = await this.client.post('/api/scene/paste-frames', req);
			const data = resp.data as api.PasteFramesIntoSceneResponse;
			return data;
		}
	);

	// DBR = De-Bounce Remote
	// This function is meant to be called frequently, as when dragging a frame.
	// The updated will be applied optimistically right away, over and over for
	// each call. After a debounce, the last update is sent to the server.
	dbrUpdateFrame = createDbrHandler({
		getObjectId: (req: api.UpdateFrameRequest & { showToast?: boolean }) => req.update.id,
		onUpdate: (writeId, req: api.UpdateFrameRequest & { showToast?: boolean }) => {
			const write: OptimisticWrite<Frame> = {
				id: writeId,
				operation: 'update',
				value: req.update,
			};
			store.dispatch(optimisticallyUpdateFrames([write]));
		},
		onRequest: async (writeId, req: api.UpdateFrameRequest & { showToast?: boolean }) => {
			const requestWithWriteId: api.UpdateFrameRequest = {
				...req,
				update: { ...req.update, writeId },
			};
			try {
				trackEndpoint('PATCH', '/api/frame');
				await this.client.patch('/api/frame', requestWithWriteId);
			} catch (e) {
				alertErrorMessageFromServer(e, req.showToast || false);
				store.dispatch(resolveOptimisticallyUpdatedFrames([writeId]));
				throw e;
			}
		},
	});

	accessFrame = alertOnError(
		async (req: api.FrameAccessRequest): Promise<api.FrameAccessResponse> => {
			const resp = await this.client.post('/api/frame/access', req);
			const data = resp.data as api.FrameAccessResponse;
			return data;
		}
	);

	getFrameSignedUrls = alertOnError(
		async (req: GetFrameSignedUrlsRequest): Promise<GetFrameSignedUrlsResponse> => {
			const resp = await this.client.post('/api/frame/signed-urls', req);
			const data = resp.data as GetFrameSignedUrlsResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// site
	//-----------------------------------------------------------------------------
	getSiteDesignSystemInfo = alertOnError(
		async (req: GetSiteDesignSystemInfoRequest): Promise<GetSiteDesignSystemInfoResponse> => {
			const resp = await this.client.get(`/api/site/${req.siteId}/design-system-info`);
			return resp.data as GetSiteDesignSystemInfoResponse;
		}
	);

	//-----------------------------------------------------------------------------
	// scene comments
	//-----------------------------------------------------------------------------
	createSceneComment = async (
		req: Omit<api.CreateSceneCommentRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		req = roundIntegerPositionFields(req);
		const write: OptimisticWrite<SceneComment> = {
			id: uuid(),
			operation: 'insert',
			value: {
				...req,
				userId: store.getState().db.user?.id || '',
				createdAt: new Date(),
				deletedAt: null,
			},
		};
		store.dispatch(optimisticallyUpdateSceneComments([write]));
		try {
			trackEndpoint('POST', '/api/scene-comment');
			await this.client.post('/api/scene-comment', { ...req, writeId: write.id });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedSceneComments([write.id]));
			throw e;
		}
	};

	deleteSceneComment = async (commentId: string, showToast: boolean = false) => {
		const write: OptimisticWrite<SceneComment> = {
			id: uuid(),
			operation: 'delete',
			value: { id: commentId },
		};
		store.dispatch(optimisticallyUpdateSceneComments([write]));
		try {
			trackEndpoint('DELETE', '/api/scene-comment');
			await this.client.delete('/api/scene-comment', { data: { commentId } });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedSceneComments([write.id]));
			throw e;
		}
	};

	updateSceneComment = async (update: PartialSceneComment, showToast: boolean = false) => {
		update = roundIntegerPositionFields(update);
		const write: OptimisticWrite<SceneComment> = {
			id: uuid(),
			operation: 'update',
			value: update,
		};
		store.dispatch(optimisticallyUpdateSceneComments([write]));
		const updateWithWriteId = { ...update, writeId: write.id };
		try {
			trackEndpoint('PATCH', '/api/scene-comment');
			await this.client.patch('/api/scene-comment', { update: updateWithWriteId });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedSceneComments([write.id]));
			throw e;
		}
	};

	//-----------------------------------------------------------------------------
	// scene chat
	//-----------------------------------------------------------------------------
	// Creates a chat and posts its first message in one request. The optimistic chat
	// mirrors the state the server lands in — the entry is already in the conversation
	// and the chat is busy answering — so the UI never shows a blank chat that isn't
	// really blank.
	createSceneChatWithEntry = async (
		req: Omit<api.CreateSceneChatWithEntryRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		req = roundIntegerPositionFields(req);
		trackCreativeAction('new_chat');
		const { entry, chatMode, style, mode, ...chatFields } = req;
		const optimisticChat: Omit<SceneChat, 'writeId'> = {
			...chatFields,
			// A real date will come from the database.
			// For the optimistic update this is good enough.
			createdAt: new Date(),
			updatedAt: new Date(),
			entries: [entry],
			metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Processing ask...' },
		};
		const write: OptimisticWrite<SceneChat> = {
			id: uuid(),
			operation: 'insert',
			value: optimisticChat,
		};
		store.dispatch(optimisticallyUpdateSceneChats([write]));
		try {
			trackEndpoint('POST', '/api/scene-chat/create-with-entry');
			await this.client.post('/api/scene-chat/create-with-entry', { ...req, writeId: write.id });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedSceneChats([write.id]));
			throw e;
		}
	};

	updateSceneChat = async (
		req: api.UpdateSceneChatRequest,
		showToast: boolean = false
	): Promise<api.UpdateSceneChatResponse> => {
		req = { ...req, update: roundIntegerPositionFields(req.update) };
		const write: OptimisticWrite<SceneChat> = {
			id: uuid(),
			operation: 'update',
			value: req.update,
		};
		store.dispatch(optimisticallyUpdateSceneChats([write]));
		const requestWithWriteId = { ...req, update: { ...req.update, writeId: write.id } };
		try {
			trackEndpoint('PATCH', '/api/scene-chat');
			const resp = await this.client.patch('/api/scene-chat', requestWithWriteId);
			const data = resp.data as api.UpdateSceneChatResponse;
			return data;
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedSceneChats([write.id]));
			throw e;
		}
	};

	stopSceneChatGeneration = alertOnError(
		async (
			req: api.StopSceneChatGenerationRequest
		): Promise<api.StopSceneChatGenerationResponse> => {
			try {
				const resp = await this.client.post('/api/scene-chat/stop-generation', req);
				const data = resp.data as api.StopSceneChatGenerationResponse;
				return data;
			} catch (e) {
				alertErrorMessageFromServer(e, false);
				throw e;
			}
		}
	);

	sceneChatFeedback = async (req: Omit<api.SceneChatFeedbackRequest, 'writeId'>) => {
		const state = store.getState();
		const sceneChat = state.db.activeSceneChat;
		const isActive = !!sceneChat && sceneChat.id === req.chatId;

		// For the active chat, apply an optimistic update through the store so the UI
		// reflects the feedback immediately. For archived/parent chats, skip the optimistic
		// store update (the caller manages local state) but still POST the feedback.
		let writeId: string | undefined;
		if (isActive && sceneChat) {
			const entries = [...sceneChat.entries];
			const entry = entries[req.entryIndex] as SystemChatEntry;
			entries[req.entryIndex] = {
				...entry,
				feedback: req.feedback,
				feedbackComment: req.comment || null,
			};

			const write: OptimisticWrite<SceneChat> = {
				id: uuid(),
				operation: 'update',
				value: { id: req.chatId, entries },
			};
			writeId = write.id;
			store.dispatch(optimisticallyUpdateSceneChats([write]));
		}

		try {
			await this.client.post('/api/scene-chat/feedback', { ...req, writeId });
		} catch (e) {
			if (writeId) {
				store.dispatch(resolveOptimisticallyUpdatedSceneChats([writeId]));
			}
			throw e;
		}
	};

	//-----------------------------------------------------------------------------
	// scene chat
	//-----------------------------------------------------------------------------
	createSceneChatFromPrompt = alertOnError(
		async (req: api.CreateSceneChatRequest): Promise<api.CreateSceneChatResponse> => {
			trackCreativeAction('new_scene_chat');
			trackEndpoint('POST', '/api/scene-chat/from-prompt');
			const resp = await this.client.post('/api/scene-chat/from-prompt', req);
			return resp.data as api.CreateSceneChatResponse;
		}
	);

	addSceneChatUserEntry = alertOnError(
		async (req: api.AddUserEntrySceneChatRequest): Promise<api.AddUserEntrySceneChatResponse> => {
			trackCreativeAction('add_user_entry');
			trackEndpoint('POST', '/api/scene-chat/add-user-entry');
			const resp = await this.client.post('/api/scene-chat/add-user-entry', req);
			return resp.data as api.AddUserEntrySceneChatResponse;
		}
	);

	//-----------------------------------------------------------------------------
	// frame chat
	//-----------------------------------------------------------------------------
	addFrameChatUserEntry = alertOnError(
		async (req: api.AddUserEntryFrameChatRequest): Promise<api.AddUserEntryFrameChatResponse> => {
			trackCreativeAction('add_frame_chat_user_entry');
			trackEndpoint('POST', '/api/frame-chat/add-user-entry');
			const resp = await this.client.post('/api/frame-chat/add-user-entry', req);
			return resp.data as api.AddUserEntryFrameChatResponse;
		}
	);

	getFrameChatId = async (req: api.GetFrameChatIdRequest): Promise<api.GetFrameChatIdResponse> => {
		const resp = await this.client.post('/api/frame-chat/get-id', req);
		return resp.data as api.GetFrameChatIdResponse;
	};

	stopFrameChatGeneration = alertOnError(
		async (
			req: api.StopFrameChatGenerationRequest
		): Promise<api.StopFrameChatGenerationResponse> => {
			try {
				const resp = await this.client.post('/api/frame-chat/stop-generation', req);
				return resp.data as api.StopFrameChatGenerationResponse;
			} catch (e) {
				alertErrorMessageFromServer(e, false);
				throw e;
			}
		}
	);

	//-----------------------------------------------------------------------------
	// design system chat
	//-----------------------------------------------------------------------------
	// Creates a DS chat and posts its first message in one request. See
	// createSceneChatWithEntry for why the optimistic chat carries the entry + busy status.
	createDsChatWithEntry = async (
		req: Omit<api.CreateDsChatWithEntryRequest, 'writeId'>,
		showToast: boolean = false
	) => {
		trackCreativeAction('new_ds_chat');
		const { entry, chatMode, ...chatFields } = req;
		const optimisticDsChat: Omit<DsChat, 'writeId'> = {
			...chatFields,
			// A real date will come from the database.
			// For the optimistic update this is good enough.
			createdAt: new Date(),
			updatedAt: new Date(),
			entries: [entry],
			metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Processing ask...' },
		};
		const write: OptimisticWrite<DsChat> = {
			id: uuid(),
			operation: 'insert',
			value: optimisticDsChat,
		};
		store.dispatch(optimisticallyUpdateDsChats([write]));
		try {
			trackEndpoint('POST', '/api/ds-chat/create-with-entry');
			await this.client.post('/api/ds-chat/create-with-entry', { ...req, writeId: write.id });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedDsChats([write.id]));
			throw e;
		}
	};

	dsChatAddUserEntry = alertOnError(
		async (req: api.AddUserEntryDsChatRequest): Promise<api.AddUserEntryDsChatResponse> => {
			trackCreativeAction('ds_add_user_entry');
			trackEndpoint('POST', '/api/ds-chat/add-user-entry');
			const resp = await this.client.post('/api/ds-chat/add-user-entry', req);
			return resp.data as api.AddUserEntryDsChatResponse;
		}
	);

	updateDsChat = async (
		req: api.UpdateDsChatRequest,
		showToast: boolean = false
	): Promise<api.UpdateDsChatResponse> => {
		const write: OptimisticWrite<DsChat> = {
			id: uuid(),
			operation: 'update',
			value: req.update,
		};
		store.dispatch(optimisticallyUpdateDsChats([write]));
		const requestWithWriteId = { ...req, update: { ...req.update, writeId: write.id } };
		try {
			trackEndpoint('PATCH', '/api/ds-chat');
			const resp = await this.client.patch('/api/ds-chat', requestWithWriteId);
			const data = resp.data as api.UpdateDsChatResponse;
			return data;
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedDsChats([write.id]));
			throw e;
		}
	};

	approveDsChatPlan = alertOnError(
		async (req: api.ApproveDsChatPlanRequest): Promise<api.ApproveDsChatPlanResponse> => {
			trackDesignSystemAction('approve_ds_chat_plan');
			trackEndpoint('POST', '/api/ds-chat/approve-plan');
			const resp = await this.client.post('/api/ds-chat/approve-plan', req);
			return resp.data as api.ApproveDsChatPlanResponse;
		}
	);

	stopDsChatGeneration = alertOnError(
		async (req: api.StopDsChatGenerationRequest): Promise<api.StopDsChatGenerationResponse> => {
			try {
				const resp = await this.client.post('/api/ds-chat/stop-generation', req);
				const data = resp.data as api.StopDsChatGenerationResponse;
				return data;
			} catch (e) {
				alertErrorMessageFromServer(e, false);
				throw e;
			}
		}
	);

	dsChatFeedback = async (req: Omit<api.DsChatFeedbackRequest, 'writeId'>) => {
		const state = store.getState();
		const dsChat = state.db.activeDsChat;
		if (!dsChat || dsChat.id !== req.dsChatId) return;

		const entries = [...dsChat.entries];
		const entry = entries[req.entryIndex] as SystemChatEntry;
		entries[req.entryIndex] = {
			...entry,
			feedback: req.feedback,
			feedbackComment: req.comment || null,
		};

		const write: OptimisticWrite<DsChat> = {
			id: uuid(),
			operation: 'update',
			value: { id: req.dsChatId, entries },
		};
		store.dispatch(optimisticallyUpdateDsChats([write]));

		try {
			await this.client.post('/api/ds-chat/feedback', { ...req, writeId: write.id });
		} catch (e) {
			store.dispatch(resolveOptimisticallyUpdatedDsChats([write.id]));
			throw e;
		}
	};

	//-----------------------------------------------------------------------------
	// design system
	//-----------------------------------------------------------------------------
	createDesignSystem = alertOnError(
		async (req: api.CreateDesignSystemRequest): Promise<api.CreateDesignSystemResponse> => {
			trackCreativeAction('new_design_system');
			const resp = await this.client.post('/api/design-system/create', req);
			const data = resp.data as api.CreateDesignSystemResponse;
			return data;
		}
	);

	// Creates a design system named from the prompt with its chat already
	// seeded; the response's title feeds the navigation slug
	createDesignSystemFromPrompt = alertOnError(
		async (
			req: api.CreateDesignSystemFromPromptRequest
		): Promise<api.CreateDesignSystemFromPromptResponse> => {
			trackCreativeAction('new_design_system');
			const resp = await this.client.post('/api/design-system/create-from-prompt', req);
			return resp.data as api.CreateDesignSystemFromPromptResponse;
		}
	);

	duplicateDesignSystem = alertOnError(
		async (req: api.DuplicateDesignSystemRequest): Promise<api.DuplicateDesignSystemResponse> => {
			const resp = await this.client.post('/api/design-system/duplicate', req);
			return resp.data as api.DuplicateDesignSystemResponse;
		}
	);

	restoreDesignSystemVersion = alertOnError(
		async (
			req: api.RestoreDesignSystemVersionRequest
		): Promise<api.RestoreDesignSystemVersionResponse> => {
			const resp = await this.client.post('/api/design-system/restore-version', req);
			return resp.data as api.RestoreDesignSystemVersionResponse;
		}
	);

	updateDesignSystem = async (req: api.UpdateDesignSystemRequest, showToast: boolean = false) => {
		const write: OptimisticWrite<DesignSystem> = {
			id: uuid(),
			operation: 'update',
			value: req.update,
		};
		store.dispatch(optimisticallyUpdateDesignSystems([write]));
		const requestWithWriteId: api.UpdateDesignSystemRequest = {
			update: { ...req.update, writeId: write.id },
		};
		try {
			trackEndpoint('PATCH', '/api/design-system');
			await this.client.patch('/api/design-system', requestWithWriteId);
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedDesignSystems([write.id]));
			throw e;
		}
	};

	discardDsVersion = alertOnError(
		async (designSystemId: string): Promise<api.DiscardDraftDesignSystemResponse> => {
			const resp = await this.client.post('/api/design-system/discard-draft', {
				designSystemId,
			});
			return resp.data as api.DiscardDraftDesignSystemResponse;
		}
	);

	publishDsVersion = alertOnError(
		async (designSystemId: string): Promise<api.PublishDraftDesignSystemResponse> => {
			const resp = await this.client.post('/api/design-system/publish-draft', {
				designSystemId,
			});
			return resp.data as api.PublishDraftDesignSystemResponse;
		}
	);

	updateDsFiles = alertOnError(
		async (
			req: api.UpdateDsFilesRequest & { designSystemId: string }
		): Promise<api.UpdateDsFilesResponse> => {
			const blocked = designSystemEditingBlocked();
			if (blocked) {
				displayDsReadOnlyModal(blocked);
				return { success: false } as any;
			}
			const { designSystemId, files } = req;
			const writes = createDsFileWrites(files);
			store.dispatch(optimisticallyUpdateDsFiles(writes));
			try {
				const resp = await this.client.post(`/api/design-system/${designSystemId}/files`, {
					files,
				});
				store.dispatch(resolveOptimisticDsFileWrites(writes.map((w) => w.id)));
				return resp.data as api.UpdateDsFilesResponse;
			} catch (e) {
				store.dispatch(resolveOptimisticDsFileWrites(writes.map((w) => w.id)));
				throw e;
			}
		}
	);

	// Saves DS files without optimistic Redux updates.
	// Use for edits where the editor already shows the correct state (e.g. markdown editing).
	// Use for situations where optimistic updates are not needed (because React component is maintaining state)
	// And a very small delay when e.g. closing and re-opening a tab is acceptable (poll cache should push clean data back in a second or two)
	saveDsFiles = async (req: api.UpdateDsFilesRequest & { designSystemId: string }) => {
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		const { designSystemId, files } = req;
		await this.client.post(`/api/design-system/${designSystemId}/files`, { files });
	};

	summarizeFileChange = alertOnError(
		async (req: api.SummarizeDsFileChangeRequest): Promise<api.SummarizeDsFileChangeResponse> => {
			const resp = await this.client.post('/api/design-system/summarize-file-change', req);
			return resp.data as api.SummarizeDsFileChangeResponse;
		}
	);

	migrateDesignSystem = alertOnError(
		async (designSystemId: string): Promise<api.MigrateDesignSystemResponse> => {
			const resp = await this.client.post('/api/design-system/migrate', { designSystemId });
			return resp.data as api.MigrateDesignSystemResponse;
		}
	);

	deleteDesignSystem = async (req: api.DeleteDesignSystemRequest, showToast: boolean = false) => {
		trackDesignSystemAction('delete');
		const write: OptimisticWrite<DesignSystem> = {
			id: uuid(),
			operation: 'delete',
			value: { id: req.designSystemId },
		};
		store.dispatch(optimisticallyUpdateDesignSystems([write]));
		try {
			await this.client.delete('/api/design-system', { data: req });
		} catch (e) {
			alertErrorMessageFromServer(e, showToast);
			store.dispatch(resolveOptimisticallyUpdatedDesignSystems([write.id]));
			throw e;
		}
	};

	getDesignSystemUsers = alertOnError(
		async (req: api.GetDesignSystemUsersRequest): Promise<api.GetDesignSystemUsersResponse> => {
			const resp = await this.client.post('/api/design-system/users', req);
			const data = resp.data as api.GetDesignSystemUsersResponse;
			return data;
		}
	);

	grantDesignSystemAccess = alertOnError(
		async (
			req: api.GrantDesignSystemAccessRequest
		): Promise<api.GrantDesignSystemAccessResponse> => {
			trackDesignSystemAction('grant_access');
			const resp = await this.client.post('/api/design-system/grant-access', req);
			const data = resp.data as api.GrantDesignSystemAccessResponse;
			return data;
		}
	);

	revokeDesignSystemAccess = alertOnError(
		async (
			req: api.RevokeDesignSystemAccessRequest
		): Promise<api.RevokeDesignSystemAccessResponse> => {
			trackDesignSystemAction('revoke_access');
			const resp = await this.client.post('/api/design-system/revoke-access', req);
			const data = resp.data as api.RevokeDesignSystemAccessResponse;
			return data;
		}
	);

	requestDesignSystemAccess = alertOnError(
		async (designSystemId: string): Promise<api.RequestDesignSystemAccessResponse> => {
			const resp = await this.client.post('/api/design-system/request-access', {
				designSystemId,
			});
			return resp.data as api.RequestDesignSystemAccessResponse;
		}
	);

	//-----------------------------------------------------------------------------
	// import
	//-----------------------------------------------------------------------------
	importImage = alertOnError(async (req: ImportImageRequest): Promise<ImportImageResponse> => {
		trackCreativeAction('import_image');
		const resp = await this.client.post('/api/import/image', req);
		const data = resp.data as ImportImageResponse;
		return data;
	});
	importFigma = alertOnError(async (req: ImportFigmaRequest): Promise<ImportFigmaResponse> => {
		trackCreativeAction('import_figma');
		const resp = await this.client.post('/api/import/figma', req);
		const data = resp.data as ImportFigmaResponse;
		return data;
	});
	importMoonshot = alertOnError(
		async (req: api.ImportMoonshotRequest): Promise<api.ImportMoonshotResponse> => {
			trackCreativeAction('import_moonshot');
			const resp = await this.client.post('/api/import/moonshot', req);
			const data = resp.data as api.ImportMoonshotResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// file
	//-----------------------------------------------------------------------------
	createFile = alertOnError(
		async (req: CreateFileRequest, signal?: AbortSignal): Promise<CreateFileResponse> => {
			const resp = await this.client.post('/api/file', req, { signal });
			const data = resp.data as CreateFileResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// transcribe
	//-----------------------------------------------------------------------------
	// No alertOnError: the voice input widget surfaces failures inline
	transcribeAudio = async (
		req: api.TranscribeAudioRequest
	): Promise<api.TranscribeAudioResponse> => {
		const resp = await this.client.post('/api/transcribe', req);
		return resp.data as api.TranscribeAudioResponse;
	};

	//-----------------------------------------------------------------------------
	// image generation
	//-----------------------------------------------------------------------------
	generateImage = alertOnError(
		async (req: ImageGenerateRequest): Promise<ImageGenerateResponse> => {
			const resp = await this.client.post('/api/image/generate', req);
			const data = resp.data as ImageGenerateResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// inline svg
	//-----------------------------------------------------------------------------
	inlineSvg = async (req: InlineSvgRequest): Promise<InlineSvgResponse> => {
		const resp = await this.client.post('/api/svg/inline', req);
		const data = resp.data as InlineSvgResponse;
		return data;
	};

	//-----------------------------------------------------------------------------
	// export
	//-----------------------------------------------------------------------------
	exportFramesCode = async (
		req: api.ExportFramesCodeRequest
	): Promise<api.ExportFramesCodeResponse> => {
		const resp = await this.client.post('/api/export/frames-code', req);
		return resp.data as api.ExportFramesCodeResponse;
	};

	exportFramesAssets = async (
		req: api.ExportFramesAssetsRequest
	): Promise<api.ExportFramesAssetsResponse> => {
		const resp = await this.client.post('/api/export/frames-assets', req);
		return resp.data as api.ExportFramesAssetsResponse;
	};

	exportFramesAi = async (req: api.ExportFramesAiRequest): Promise<api.ExportFramesAiResponse> => {
		const resp = await this.client.post('/api/export/frames-ai', req);
		return resp.data as api.ExportFramesAiResponse;
	};

	exportDsAi = async (req: api.ExportDsAiRequest): Promise<api.ExportDsAiResponse> => {
		const resp = await this.client.post('/api/export/ds-ai', req);
		return resp.data as api.ExportDsAiResponse;
	};

	exportPrepareSvgAssets = async (
		req: api.ExportPrepareSvgAssetsRequest
	): Promise<api.ExportPrepareSvgAssetsResponse> => {
		const resp = await this.client.post('/api/export/prepare-svg-assets', req);
		return resp.data as api.ExportPrepareSvgAssetsResponse;
	};

	exportStageFrameSvg = async (
		req: api.ExportStageFrameSvgRequest
	): Promise<api.ExportStageFrameSvgResponse> => {
		const resp = await this.client.post('/api/export/stage-frame-svg', req);
		return resp.data as api.ExportStageFrameSvgResponse;
	};

	exportFailSvgAssets = async (
		req: api.ExportFailSvgAssetsRequest
	): Promise<api.ExportFailSvgAssetsResponse> => {
		const resp = await this.client.post('/api/export/fail-svg-assets', req);
		return resp.data as api.ExportFailSvgAssetsResponse;
	};

	exportPackageSvgAssets = async (
		req: api.ExportPackageSvgAssetsRequest
	): Promise<api.ExportPackageSvgAssetsResponse> => {
		const resp = await this.client.post('/api/export/package-svg-assets', req);
		return resp.data as api.ExportPackageSvgAssetsResponse;
	};

	//-----------------------------------------------------------------------------
	// style
	//-----------------------------------------------------------------------------
	createStyle = alertOnError(async (req: CreateStyleRequest): Promise<CreateStyleResponse> => {
		trackCreativeAction('new_style');
		const resp = await this.client.post('/api/style', req);
		const data = resp.data as CreateStyleResponse;
		return data;
	});

	//-----------------------------------------------------------------------------
	// fonts
	//-----------------------------------------------------------------------------
	getGoogleFonts = alertOnError(async (): Promise<GoogleFont[]> => {
		if (this.cachedFonts) {
			return this.cachedFonts;
		}

		const response = await this.client.get<GoogleFont[]>('/font');
		this.cachedFonts = response.data;
		return this.cachedFonts;
	});

	//-----------------------------------------------------------------------------
	// organizations
	//-----------------------------------------------------------------------------
	createOrganization = alertOnError(
		async (req: api.CreateOrganizationRequest): Promise<api.CreateOrganizationResponse> => {
			const resp = await this.client.post('/api/organization', req);
			const data = resp.data as api.CreateOrganizationResponse;
			return data;
		}
	);

	getOrganization = alertOnError(
		async (organizationId: string): Promise<api.GetOrganizationResponse> => {
			const resp = await this.client.get(`/api/organization/${organizationId}`);
			const data = resp.data as api.GetOrganizationResponse;
			return data;
		}
	);

	updateOrganization = alertOnError(
		async (
			organizationId: string,
			req: { update: PartialOrganization }
		): Promise<api.UpdateOrganizationResponse> => {
			const resp = await this.client.patch('/api/organization', {
				organizationId,
				update: req.update,
			});
			const data = resp.data as api.UpdateOrganizationResponse;
			return data;
		}
	);

	deleteOrganization = alertOnError(
		async (organizationId: string): Promise<{ success: boolean }> => {
			const resp = await this.client.delete('/api/organization', {
				data: { organizationId },
			});
			const data = resp.data as { success: boolean };
			return data;
		}
	);

	getOrganizationUsers = alertOnError(
		async (organizationId: string): Promise<api.GetOrganizationUsersResponse> => {
			const resp = await this.client.post('/api/organization/users', { organizationId });
			const data = resp.data as api.GetOrganizationUsersResponse;
			return data;
		}
	);

	addOrganizationUser = alertOnError(
		async (organizationId: string, email: string): Promise<api.AddOrganizationUserResponse> => {
			const resp = await this.client.post('/api/organization/user', {
				organizationId,
				email,
			});
			const data = resp.data as api.AddOrganizationUserResponse;
			return data;
		}
	);

	removeOrganizationUser = alertOnError(
		async (
			organizationId: string,
			userId?: string,
			email?: string
		): Promise<api.RemoveOrganizationUserResponse> => {
			const resp = await this.client.delete('/api/organization/user', {
				data: { organizationId, userId, email },
			});
			const data = resp.data as api.RemoveOrganizationUserResponse;
			return data;
		}
	);

	leaveOrganization = alertOnError(
		async (organizationId: string): Promise<api.LeaveOrganizationResponse> => {
			const resp = await this.client.post('/api/organization/leave', { organizationId });
			const data = resp.data as api.LeaveOrganizationResponse;
			return data;
		}
	);

	//-----------------------------------------------------------------------------
	// mcp
	//-----------------------------------------------------------------------------
	createMcpToken = alertOnError(async (name: string): Promise<api.CreateMcpTokenResponse> => {
		const body: api.CreateMcpTokenRequest = { name };
		const resp = await this.client.post('/api/mcp/tokens', body);
		return resp.data as api.CreateMcpTokenResponse;
	});

	listMcpTokens = alertOnError(async (): Promise<api.ListMcpTokensResponse> => {
		const resp = await this.client.get('/api/mcp/tokens');
		return resp.data as api.ListMcpTokensResponse;
	});

	revokeMcpToken = alertOnError(async (id: string): Promise<api.RevokeMcpTokenResponse> => {
		const resp = await this.client.delete(`/api/mcp/tokens/${id}`);
		return resp.data as api.RevokeMcpTokenResponse;
	});
}

export default new Forge();
